// A stuck sign-in. Firebase renews the session about once an hour; when that
// renewal hangs (corrupt site data, a bad network moment), every Firestore read
// waits on it and the whole app sits on "Loading…", even across reloads. Only
// signing in again clears it, so when the check below doesn't come back, the
// app signs the user out and sends them to the sign-in page.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { doc, getDocFromServer } from "firebase/firestore";
import { auth, db } from "../firebase";

/** How long the check gets before the session counts as stuck */
export const SESSION_TIMEOUT_MS = 20_000;
/** Check again when the tab comes back after being hidden this long (a laptop asleep) */
const RECHECK_AFTER_HIDDEN_MS = 5 * 60_000;

export type SessionState = "ok" | "stuck" | "unknown";

/**
 * Whether the session works: `probe` renews it and reads one document.
 * "stuck": it didn't finish in time, or failed for a sign-in reason.
 * "unknown": it failed some other way (offline, Firestore down); leave the user signed in.
 */
export async function checkSession(probe: () => Promise<unknown>, timeoutMs = SESSION_TIMEOUT_MS): Promise<SessionState> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), timeoutMs);
  });
  try {
    const result = await Promise.race([probe().then(() => "done" as const), timeout]);
    return result === "timeout" ? "stuck" : "ok";
  } catch (e: any) {
    const code = String(e?.code ?? "");
    return code.startsWith("auth/") || code === "unauthenticated" ? "stuck" : "unknown";
  } finally {
    clearTimeout(timer);
  }
}

/** Renew the session, then read the user's own doc from the server */
async function probe(uid: string) {
  const user = auth.currentUser;
  if (!user || user.uid !== uid) return;
  await user.getIdToken();
  await getDocFromServer(doc(db, "users", uid));
}

/** Signs the user out when their session is stuck: on load, and when the tab comes back after a while */
export function useStuckSessionSignOut(uid: string | null) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let hiddenAt: number | null = null;

    const run = async () => {
      if (!navigator.onLine) return;
      const state = await checkSession(() => probe(uid));
      if (cancelled || state !== "stuck" || auth.currentUser?.uid !== uid) return;
      console.warn("Session stuck: signing out");
      await signOut(auth).catch(() => {});
      navigate("/login", { replace: true });
    };
    const onVisibility = () => {
      if (document.hidden) hiddenAt = Date.now();
      else if (hiddenAt !== null && Date.now() - hiddenAt > RECHECK_AFTER_HIDDEN_MS) run();
    };

    run();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [uid, navigate]);
}
